// Company Switcher Service for Multi-Company Access
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

export interface Company {
  id: string;
  name: string;
  code: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface AccessibleCompaniesResponse {
  success: boolean;
  data: {
    companies: Company[];
    activeCompanyId: string | null;
  };
}

export interface CurrentCompanyResponse {
  success: boolean;
  data: {
    company: Company | null;
    companyId: string | null;
  };
}

export interface SwitchCompanyResponse {
  success: boolean;
  message: string;
  data: {
    activeCompanyId: string;
    company: Company;
  };
}

class CompanySwitcherService {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  async getAccessibleCompanies(): Promise<AccessibleCompaniesResponse> {
    const response = await fetch(`${this.baseURL}/company-switcher/accessible`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch accessible companies');
    }

    return response.json();
  }

  async getCurrentCompany(): Promise<CurrentCompanyResponse> {
    const response = await fetch(`${this.baseURL}/company-switcher/current`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch current company');
    }

    return response.json();
  }

  async switchCompany(companyId: string): Promise<SwitchCompanyResponse> {
    const response = await fetch(`${this.baseURL}/company-switcher/switch`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to switch company');
    }

    return response.json();
  }
}

export const companySwitcherService = new CompanySwitcherService(API_BASE_URL);
